/**
 * components/layout/Sidebar.tsx
 *
 * Main navigation sidebar.
 * - Regular users: Accueil, Formulaires, Clients, Paramètres
 * - Admins: Accueil, Formulaires, Utilisateurs, Paramètres
 *   (no "Administration" sub-section header, no Clients link for admins)
 * Updated 2026-03-11: flattened admin nav, removed Administration grouping,
 * hidden Clients for admin users.
 * Updated 2026-03-12: display name and initials now use userProfile.displayName
 *   and userProfile.initials (computed from first/last name in AuthContext).
 * Updated 2026-03-12: added admin role indicator - red pill badge next to email
 *   in footer user card (visible only when isAdmin).
 * Updated 2026-04-07: replaced FileText icon + text header with logo-qollect.svg image.
 * Updated 2026-04-16: removed NotificationBell; unread assignment indicator moved to FormsTable.
 * Updated 2026-04-16: added subtle "BETA" badge next to the Qollect logo in the header.
 * Updated 2026-05-11: added discreet Health Check icon button above the footer border (admin only).
 * Updated 2026-06-09: in impersonation mode, show user nav (not admin nav) and display
 *   the impersonated user's identity in the footer with a dashed amber border indicator.
 * Updated 2026-06-10: uses currentBannerHeight from context (dynamic, responds to collapse).
 * Updated 2026-06-19: removed Catalogue menu item (catalog system removed).
 */

import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import {
  Home,
  FileText,
  Users,
  Settings,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number;
  adminOnly?: boolean;
  hideForAdmin?: boolean;
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, userProfile } = useAuth();
  const { isImpersonating, impersonatedUser, effectiveUserId, currentBannerHeight } = useImpersonation();
  const [newCompletedCount, setNewCompletedCount] = useState(0);

  useEffect(() => {
    fetchNewCompletedCount();
  }, [effectiveUserId]);

  const fetchNewCompletedCount = async () => {
    const targetUserId = effectiveUserId;
    if (!targetUserId) return;

    const { count } = await supabase
      .from('requests')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', targetUserId)
      .eq('status', 'completed')
      .is('viewed_at', null);

    setNewCompletedCount(count || 0);
  };

  const getColorFromEmail = (email: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-pink-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500',
      'bg-emerald-500',
      'bg-rose-500'
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // When impersonating, show user nav (not admin nav)
  const isAdmin = isImpersonating ? false : (userProfile?.isAdmin ?? false);

  const allMenuItems: MenuItem[] = [
    { id: 'dashboard', label: 'Accueil', icon: Home, path: '/dashboard' },
    { id: 'my-forms', label: 'Formulaires', icon: FileText, path: '/my-forms' },
    {
      id: 'clients',
      label: 'Clients',
      icon: Users,
      path: '/clients',
      hideForAdmin: true,
      badge: newCompletedCount > 0 ? newCompletedCount : undefined,
    },
    { id: 'admin-users', label: 'Utilisateurs', icon: Users, path: '/admin/users', adminOnly: true },
    { id: 'settings', label: 'Paramètres', icon: Settings, path: '/settings' },
  ];

  const menuItems = allMenuItems.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.hideForAdmin && isAdmin) return false;
    return true;
  });

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isActiveItem = (item: MenuItem) => {
    if (item.adminOnly || item.path === '/my-forms' || item.path === '/clients') {
      return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    }
    return isActive(item.path);
  };

  // Footer identity: show impersonated user when active, otherwise real user
  const footerEmail = isImpersonating ? impersonatedUser!.email : (userProfile?.email ?? '');
  const footerDisplayName = isImpersonating ? impersonatedUser!.displayName : (userProfile?.displayName ?? '');
  const footerInitials = isImpersonating ? impersonatedUser!.initials : (userProfile?.initials ?? '');

  return (
    <aside
      className="w-[280px] fixed left-0 bg-white border-r border-neutral-200 flex flex-col"
      style={{ top: currentBannerHeight, height: `calc(100vh - ${currentBannerHeight}px)` }}
    >
      {/* Header */}
      <div className="px-6 py-5 flex items-center gap-2">
        <img src="/logo-qollect.svg" alt="Qollect" className="h-10 w-auto" />
        <span className="px-1.5 py-0.5 rounded-full bg-neutral-100 border border-neutral-200 text-neutral-500 text-[10px] font-semibold tracking-wide leading-none">
          BETA
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-6 overflow-y-auto">
        <ul className="space-y-0.5">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActiveItem(item);

            return (
              <li key={item.id} className="relative">
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-600 rounded-r-full" />
                )}
                <button
                  onClick={() => handleNavigation(item.path)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all
                    ${active
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : 'text-neutral-700 hover:bg-neutral-50 font-normal'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && item.badge > 0 && (
                    <span className="bg-primary-100 text-primary-600 text-xs font-semibold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Health Check shortcut — real admin only (not when impersonating) */}
      {!isImpersonating && userProfile?.isAdmin && (
        <div className="px-6 pb-2 flex justify-end">
          <Link
            to="/admin/health-check"
            title="Health Check"
            aria-label="Health Check"
            className={`p-1.5 rounded-lg transition-colors ${
              location.pathname === '/admin/health-check'
                ? 'text-primary-600 bg-primary-50'
                : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-50'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span className="sr-only">Health Check</span>
          </Link>
        </div>
      )}

      {/* Footer with User Profile and Logout */}
      <div className="border-t border-neutral-100">
        {(userProfile || isImpersonating) && (
          <button
            onClick={() => !isImpersonating && handleNavigation('/settings')}
            className={`w-full px-6 py-4 flex items-center gap-3 transition-all ${isImpersonating ? 'cursor-default' : ''}`}
          >
            <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${isImpersonating ? 'border-2 border-dashed border-amber-400 bg-amber-50' : getColorFromEmail(footerEmail)}`}>
              <span className={`font-medium text-sm ${isImpersonating ? 'text-amber-700' : 'text-white'}`}>
                {footerInitials}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {footerDisplayName}
                </p>
                {!isImpersonating && userProfile?.isAdmin && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-medium leading-none flex-shrink-0">
                    <ShieldCheck className="w-3 h-3" />
                    Admin
                  </span>
                )}
              </div>
              <p className="text-xs text-neutral-500 truncate">
                {footerEmail}
              </p>
            </div>
          </button>
        )}

        <div className="px-6 pb-6">
          {!isImpersonating && (
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-neutral-700 hover:bg-neutral-50 font-normal transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span>Déconnexion</span>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
